import { defineStore } from 'pinia';
import { ref } from 'vue';
import { apiFetch } from '../services/api';

export interface DeviceValueOptions {
  enum_options: Array<{ value: unknown; label: string }>;
  min_value: number | null;
  max_value: number | null;
  minValue: number | null;
  maxValue: number | null;
  step: number | null;
  delta_value: number | null;
  deltaValue: number | null;
  unit: string | null;
}

const EMPTY_OPTIONS: DeviceValueOptions = {
  enum_options: [],
  min_value: null,
  max_value: null,
  minValue: null,
  maxValue: null,
  step: null,
  delta_value: null,
  deltaValue: null,
  unit: null,
};

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * A value's options row as the hub sends it: snake_case on the hub, camelCase
 * through the cloud gateway.
 */
interface RawValueOptions {
  min_value?: unknown;
  minValue?: unknown;
  max_value?: unknown;
  maxValue?: unknown;
  step?: unknown;
  delta_value?: unknown;
  deltaValue?: unknown;
  enum_options?: unknown;
  enumOptions?: unknown;
  unit?: unknown;
}

function normalizeOptions(raw?: RawValueOptions): DeviceValueOptions {
  const minValue = numberOrNull(raw?.min_value ?? raw?.minValue);
  const maxValue = numberOrNull(raw?.max_value ?? raw?.maxValue);
  const step = numberOrNull(raw?.step ?? raw?.delta_value ?? raw?.deltaValue);

  return {
    enum_options: Array.isArray(raw?.enum_options)
      ? raw.enum_options
      : Array.isArray(raw?.enumOptions)
        ? raw.enumOptions
        : [],
    min_value: minValue,
    max_value: maxValue,
    minValue,
    maxValue,
    step,
    delta_value: step,
    deltaValue: step,
    unit: typeof raw?.unit === 'string' ? raw.unit : null,
  };
}

function uniqueIds(ids: Iterable<unknown>): string[] {
  return Array.from(
    new Set(
      Array.from(ids)
        .map((id) => String(id ?? '').trim())
        .filter(Boolean),
    ),
  );
}

export const useDeviceValueOptionsStore = defineStore('deviceValueOptions', () => {
  const cache = ref<Record<string, DeviceValueOptions>>({});
  const inFlight = new Map<string, Promise<DeviceValueOptions>>();

  async function fetchOptions(valueId: string, force = false): Promise<DeviceValueOptions> {
    if (!force && cache.value[valueId]) return cache.value[valueId];
    if (!force && inFlight.has(valueId)) return inFlight.get(valueId)!;

    const request = apiFetch(`/devices/values/${encodeURIComponent(valueId)}/options`)
      .then((raw) => {
        const normalized = normalizeOptions(raw);
        cache.value[valueId] = normalized;
        return normalized;
      })
      .catch(() => {
        cache.value[valueId] = EMPTY_OPTIONS;
        return EMPTY_OPTIONS;
      })
      .finally(() => {
        inFlight.delete(valueId);
      });

    inFlight.set(valueId, request);
    return request;
  }

  async function fetchOptionsBulk(
    valueIds: Iterable<unknown>,
    force = false,
  ): Promise<Record<string, DeviceValueOptions>> {
    const ids = uniqueIds(valueIds);
    if (ids.length === 0) return {};

    const idsToFetch = ids.filter((id) => force || (!cache.value[id] && !inFlight.has(id)));
    const pending = ids
      .map((id) => inFlight.get(id))
      .filter((request): request is Promise<DeviceValueOptions> => Boolean(request));

    if (idsToFetch.length > 0) {
      const bulkRequest = apiFetch<Record<string, RawValueOptions>>(
        `/devices/values/options?ids=${encodeURIComponent(idsToFetch.join(','))}`,
      )
        .then((response) => {
          for (const id of idsToFetch) {
            cache.value[id] = normalizeOptions(response?.[id]);
          }
        })
        .catch(() => {
          for (const id of idsToFetch) {
            cache.value[id] = EMPTY_OPTIONS;
          }
        })
        .finally(() => {
          for (const id of idsToFetch) {
            inFlight.delete(id);
          }
        });

      for (const id of idsToFetch) {
        inFlight.set(
          id,
          bulkRequest.then(() => cache.value[id] ?? EMPTY_OPTIONS),
        );
      }

      pending.push(bulkRequest.then(() => EMPTY_OPTIONS));
    }

    if (pending.length > 0) {
      await Promise.allSettled(pending);
    }

    return Object.fromEntries(ids.map((id) => [id, cache.value[id] ?? EMPTY_OPTIONS]));
  }

  function getOptions(valueId: string): DeviceValueOptions | null {
    return cache.value[valueId] ?? null;
  }

  function clear() {
    cache.value = {};
    inFlight.clear();
  }

  return {
    cache,
    fetchOptions,
    fetchOptionsBulk,
    getOptions,
    clear,
  };
});
